import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  useSEO({ title: "Forgot Password", description: "Reset your CardMyDog password.", path: "/forgot-password" });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center" data-testid="forgot-password-sent">
        <div className="max-w-sm w-full mx-auto px-4 text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: "rgba(245,166,35,0.1)" }}>
            <Mail size={24} style={{ color: "var(--color-gold)" }} />
          </div>
          <h1 className="font-heading text-3xl uppercase tracking-tight text-[var(--color-text-dark)] mb-3">Check your email</h1>
          <p className="font-body text-sm text-[var(--color-text-mid)] mb-6">If an account exists for {email}, we've sent a password reset link. It expires in 1 hour.</p>
          <Link to="/login" className="font-body text-sm" style={{ color: "var(--color-gold)" }}>Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 flex items-center justify-center" data-testid="forgot-password-page">
      <div className="max-w-sm w-full mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl uppercase tracking-tight text-[var(--color-text-dark)] mb-2">Forgot password</h1>
          <p className="font-body text-sm text-[var(--color-text-mid)]">Enter your email and we'll send a reset link</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-6 space-y-4">
          <div>
            <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-[8px]" data-testid="forgot-email-input" />
          </div>
          <button type="submit" disabled={loading} className="w-full font-heading tracking-widest uppercase py-3 rounded-[8px] transition-colors disabled:opacity-50" style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }} data-testid="forgot-submit-btn">
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
        </form>
        <p className="text-center mt-6 font-body text-sm text-[var(--color-text-mid)]">
          <Link to="/login" style={{ color: "var(--color-gold)" }}>Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
